import React, { useEffect } from "react";

//
// 参照の変化をDebugするためのUtility Hooks
//

export const useLogOnChange = (name: string, value: unknown) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    console.log(`OnChange [${name}]`, value);
  }, [value]);
};

export const useLoggingEffect = (
  effect: React.EffectCallback,
  deps: React.DependencyList,
  name: string,
): void => {
  const previousDeps = React.useRef<React.DependencyList>();

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  React.useEffect(() => {
    const changedDeps = deps
      .map((dep, i) => ({ dep, i }))
      .filter(({ dep, i }) => dep !== previousDeps.current?.[i]);
    if (deps.length > 0 && changedDeps.length === 0) {
      return;
    }
    console.log(`[DEBUG:useEffect] ${name} Executed`, {
      changedDeps,
      deps,
      previousDeps: previousDeps.current,
    });
    previousDeps.current = deps;
    return effect();
  }, deps);
};

export const useLoggingMemo = <T>(
  factory: () => T,
  deps: React.DependencyList,
  name: string,
): T => {
  const previousDeps = React.useRef<React.DependencyList>();
  const value = React.useMemo(factory, deps);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  React.useEffect(() => {
    const changedDeps = deps
      .map((dep, i) => ({ dep, i }))
      .filter(({ dep, i }) => dep !== previousDeps.current?.[i]);
    if (deps.length > 0 && changedDeps.length === 0) {
      return;
    }
    console.log(`[DEBUG:useMemo] ${name} Changed`, {
      changedDeps,
      deps,
      previousDeps: previousDeps.current,
      value,
    });
    previousDeps.current = deps;
  }, deps);
  return value;
};

// biome-ignore lint/suspicious/noExplicitAny:
export const useLoggingCallback = <T extends (...args: any[]) => any>(
  callback: T,
  deps: React.DependencyList,
  name: string,
): T => {
  const previousDeps = React.useRef<React.DependencyList>();
  const value = React.useCallback(callback, deps);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  React.useEffect(() => {
    const changedDeps = deps
      .map((dep, i) => ({ dep, i }))
      .filter(({ dep, i }) => dep !== previousDeps.current?.[i]);
    if (deps.length > 0 && changedDeps.length === 0) {
      return;
    }
    console.log(`[DEBUG:useCallback] ${name} Changed`, {
      changedDeps,
      deps,
      previousDeps: previousDeps.current,
    });
    previousDeps.current = deps;
  }, deps);
  return value;
};

export const useExecOnlyOnce = (effect: () => void, name: string): void => {
  const executedRef = React.useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  React.useEffect(() => {
    if (!executedRef.current) {
      console.log(`[DEBUG:useExecOnlyOnce] ${name} Executed`, {
        executedRef: executedRef.current,
      });
      effect();
    }
    executedRef.current = true;
  }, []);
};
